import { formatHumanName, type MedplumClient } from '@medplum/core'
import type { DocumentReference, Patient } from '@medplum/fhirtypes'

import { logError } from './errorUtils'

export const PATIENT_IDENTITY_DOCUMENT_TYPE_SYSTEM = 'https://behuman.health/document-type'
export const PATIENT_IDENTITY_DOCUMENT_TYPE_CODE = 'PatientIdentityFront'
export const PATIENT_IDENTITY_DOCUMENT_CATEGORY_SYSTEM = 'https://behuman.health/document-category'
export const PATIENT_IDENTITY_DOCUMENT_CATEGORY_CODE = 'identity-verification'
export const PATIENT_IDENTITY_DOCUMENT_DISPLAY = 'Driver License / State ID'
export const MAX_PATIENT_IDENTITY_IMAGE_SIZE_BYTES = 10 * 1024 * 1024

const ACCEPTED_PATIENT_IDENTITY_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png'])

export type PatientIdentityUploadResult = {
  document: DocumentReference
  supersedeFailed: boolean
}

export function getPatientIdentityImageContentType(file: File): string | null {
  if (ACCEPTED_PATIENT_IDENTITY_IMAGE_TYPES.has(file.type)) {
    return file.type === 'image/jpg' ? 'image/jpeg' : file.type
  }

  return null
}

export function validatePatientIdentityImageFile(file: File): string | null {
  if (!getPatientIdentityImageContentType(file)) {
    return 'Only JPG and PNG files are allowed.'
  }

  if (file.size > MAX_PATIENT_IDENTITY_IMAGE_SIZE_BYTES) {
    return 'ID image must be 10 MB or smaller.'
  }

  return null
}

export async function fetchLatestPatientIdentityDocument(
  medplum: MedplumClient,
  patientId: string,
): Promise<DocumentReference | null> {
  const documents = await fetchCurrentPatientIdentityDocuments(medplum, patientId)
  return documents.sort(compareDocumentsByLatestDate)[0] ?? null
}

async function fetchCurrentPatientIdentityDocuments(
  medplum: MedplumClient,
  patientId: string,
): Promise<DocumentReference[]> {
  const typeToken = encodeURIComponent(
    `${PATIENT_IDENTITY_DOCUMENT_TYPE_SYSTEM}|${PATIENT_IDENTITY_DOCUMENT_TYPE_CODE}`,
  )
  return medplum.searchResources(
    'DocumentReference',
    `subject=Patient/${patientId}&status=current&type=${typeToken}&_sort=-date&_count=100`,
  )
}

export async function uploadPatientIdentityDocument(
  medplum: MedplumClient,
  file: File,
  patient: Patient,
): Promise<PatientIdentityUploadResult> {
  if (!patient.id) {
    throw new Error('Patient id is required.')
  }

  const validationError = validatePatientIdentityImageFile(file)
  if (validationError) {
    throw new Error(validationError)
  }

  const previousDocuments = await fetchCurrentPatientIdentityDocuments(medplum, patient.id)
  const contentType = getPatientIdentityImageContentType(file) as string
  const binary = await medplum.createBinary({ data: file, contentType })
  const patientDisplay = patient.name?.[0] ? formatHumanName(patient.name[0]) : undefined

  const documentInput: DocumentReference = {
    resourceType: 'DocumentReference',
    status: 'current',
    docStatus: 'final',
    subject: {
      reference: `Patient/${patient.id}`,
      display: patientDisplay,
    },
    date: new Date().toISOString(),
    description: PATIENT_IDENTITY_DOCUMENT_DISPLAY,
    securityLabel: [
      {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/v3-Confidentiality',
            code: 'R',
            display: 'restricted',
          },
        ],
        text: 'restricted',
      },
    ],
    category: [
      {
        coding: [
          {
            system: PATIENT_IDENTITY_DOCUMENT_CATEGORY_SYSTEM,
            code: PATIENT_IDENTITY_DOCUMENT_CATEGORY_CODE,
            display: 'Identity Verification',
          },
        ],
        text: 'Identity Verification',
      },
    ],
    type: {
      coding: [
        {
          system: PATIENT_IDENTITY_DOCUMENT_TYPE_SYSTEM,
          code: PATIENT_IDENTITY_DOCUMENT_TYPE_CODE,
          display: PATIENT_IDENTITY_DOCUMENT_DISPLAY,
        },
      ],
      text: PATIENT_IDENTITY_DOCUMENT_DISPLAY,
    },
    content: [
      {
        attachment: {
          contentType,
          url: `Binary/${binary.id}`,
          title: file.name || PATIENT_IDENTITY_DOCUMENT_DISPLAY,
          size: file.size,
        },
      },
    ],
  }

  let document: DocumentReference
  try {
    document = await medplum.createResource<DocumentReference>(documentInput)
  } catch (error) {
    await cleanupPatientIdentityBinary(medplum, binary.id)
    throw error
  }

  let supersedeFailed = false
  try {
    await supersedePatientIdentityDocuments(medplum, previousDocuments)
  } catch (error) {
    supersedeFailed = true
    logError('patient', 'Supersede previous patient identity documents', error)
  }
  return { document, supersedeFailed }
}

async function cleanupPatientIdentityBinary(
  medplum: MedplumClient,
  binaryId: string | undefined,
): Promise<void> {
  if (!binaryId) {
    return
  }

  try {
    await medplum.deleteResource('Binary', binaryId)
  } catch (error) {
    // Cleanup is best-effort because some clinical roles can create/update Binary but not delete it.
    logError('patient', 'Cleanup patient identity binary after upload failure', error)
  }
}

export async function deletePatientIdentityDocument(
  medplum: MedplumClient,
  document: DocumentReference,
): Promise<void> {
  await supersedePatientIdentityDocuments(medplum, [document])
}

async function supersedePatientIdentityDocuments(
  medplum: MedplumClient,
  documents: DocumentReference[],
): Promise<void> {
  const results = await Promise.allSettled(
    documents
      .filter((document) => document.id && document.status === 'current')
      .map((document) =>
        medplum.updateResource<DocumentReference>({
          ...document,
          status: 'superseded',
        }),
      ),
  )

  const failures = results.filter(
    (result): result is PromiseRejectedResult => result.status === 'rejected',
  )
  if (failures.length > 0) {
    throw new AggregateError(
      failures.map((failure) => failure.reason),
      `Failed to supersede ${failures.length} of ${results.length} identity document(s)`,
    )
  }
}

function compareDocumentsByLatestDate(a: DocumentReference, b: DocumentReference): number {
  return getDocumentTimestamp(b) - getDocumentTimestamp(a)
}

function getDocumentTimestamp(document: DocumentReference): number {
  const dateValue = document.date || document.meta?.lastUpdated
  if (!dateValue) {
    return 0
  }

  const timestamp = new Date(dateValue).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}
